// src/pages/EnglishPracticeSessionDetailPage.js
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GetEnglishPracticeSessionDetails } from "../services/englishPracticeService";
import Loader from "../components/Loader";
import DashboardCard from "../components/DashboardCard";
import {
  LanguageIcon,
  ArrowLeftIcon,
  RefreshCwIcon,
} from "../components/Icons";
import FeedbackSection from "../components/FeedbackSection"; // Assuming this is the accessible version
import { OBJECT_BASE_URL } from "../config";

const EnglishPracticeSessionDetailPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [sessionDetails, setSessionDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const errorDisplayRef = useRef(null);
  const mainContentRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setError("Session ID is missing.");
      setIsLoading(false);
      return;
    }

    const fetchDetails = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const details = await GetEnglishPracticeSessionDetails(
          Number(sessionId),
        );
        if (details.error) {
          setError(details.error);
          setSessionDetails(null);
        } else {
          setSessionDetails(details);
          if (mainContentRef.current) {
            mainContentRef.current.focus();
          }
        }
      } catch (err) {
        console.error(`Error fetching details for session ${sessionId}:`, err);
        setError("Failed to load session details. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [sessionId]);

  useEffect(() => {
    if (error && errorDisplayRef.current) {
      errorDisplayRef.current.focus();
    }
  }, [error]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const proficiencyLevelToString = (proficiencyLevel) => {
    switch (proficiencyLevel) {
      case "Beginner":
        return "Beginner";
      case "LowIntermediate":
        return "Low Intermediate";
      case "Intermediate":
        return "Intermediate";
      case "UpperIntermediate":
        return "Upper Intermediate";
      case "Advanced":
        return "Advanced";
      case "Proficient":
        return "Proficient";
      default:
        return "Unknown";
    }
  };

  const getScoreColor = (score) => {
    if (score === null || score === undefined) return "text-slate-300"; // Adjusted for better visibility
    if (score >= 8) return "text-green-300";
    if (score >= 6) return "text-yellow-300";
    return "text-red-300";
  };

  const parseJsonSafely = (jsonString) => {
    try {
      return typeof jsonString === "string"
        ? JSON.parse(jsonString)
        : jsonString;
    } catch (e) {
      console.warn("Failed to parse JSON:", e);
      return null;
    }
  };

  const renderTaskContent = (task) => {
    const content = parseJsonSafely(task.jsonContent);
    if (!content)
      return (
        <p className="text-slate-400 italic">No task content available.</p>
      );

    if (task.taskType === "read_aloud" && content.textToRead) {
      return (
        <p className="whitespace-pre-wrap text-slate-200">
          {content.textToRead}
        </p>
      );
    }

    if (task.taskType === "sentence_repetition" && content.sentencesToRepeat) {
      return (
        <div className="space-y-2">
          <p className="text-slate-200 font-medium">Sentences to repeat:</p>
          <ul className="list-disc list-inside ml-4">
            {content.sentencesToRepeat.map((sentence, index) => (
              <li key={index} className="text-slate-200">
                {sentence}
              </li>
            ))}
          </ul>
        </div>
      );
    }

    if (task.taskType === "question_answer" && content.questionToAnswer) {
      return (
        <p className="text-slate-200">
          <strong className="font-medium">Question: </strong>
          {content.questionToAnswer}
        </p>
      );
    }

    if (task.taskType === "scenario_response" && content.scenarioDescription) {
      return (
        <p className="text-slate-200">
          <strong className="font-medium">Scenario: </strong>
          {content.scenarioDescription}
        </p>
      );
    }

    if (task.taskType === "topic_discussion_prompt" && content.topicPrompt) {
      return (
        <p className="text-slate-200">
          <strong className="font-medium">Topic: </strong>
          {content.topicPrompt}
        </p>
      );
    }
    return (
      <p className="text-slate-400 italic">
        Detailed content for this task type is not displayed in this view.
        Please refer to the original practice session if available.
      </p>
    );
  };

  const renderSpeechFeedback = (task) => {
    const speechFeedback = parseJsonSafely(task.speechFeedback);
    const richTranscription = parseJsonSafely(task.richTranscription);

    if (!speechFeedback && !richTranscription) {
      return (
        <p className="text-slate-400 italic mt-3">
          {" "}
          {/* Adjusted contrast */}
          No feedback available for this task.
        </p>
      );
    }

    return (
      <div className="mt-4 space-y-4">
        {richTranscription?.fullTranscript && (
          <div className="mb-3">
            <h4 className="font-semibold text-slate-100 mb-1">
              Your Response:
            </h4>{" "}
            {/* Adjusted contrast and heading */}
            <blockquote className="text-slate-300 bg-slate-800 p-3 rounded-md text-sm italic border-l-4 border-slate-600">
              "{richTranscription.fullTranscript}"
            </blockquote>
            {task.audioUrl && (
              <div className="mt-2">
                <audio
                  controls
                  src={OBJECT_BASE_URL + task.audioUrl}
                  className="w-full h-8"
                />
              </div>
            )}
          </div>
        )}

        {speechFeedback && (
          <div className="space-y-3">
            <h4 className="font-semibold text-lg text-emerald-300 mb-2">
              Speech Analysis:
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-slate-700/60 p-3 rounded-md">
                <p className="font-medium text-slate-100">Overall Clarity:</p>
                <p
                  className={`${getScoreColor(
                    speechFeedback.overallClarityScore,
                  )} text-2xl font-bold`}
                >
                  {speechFeedback.overallClarityScore !== null &&
                  speechFeedback.overallClarityScore !== undefined
                    ? `${speechFeedback.overallClarityScore}/10`
                    : "N/A"}
                </p>
                <p className="text-xs text-slate-400">
                  {speechFeedback.overallClarityDescription}
                </p>
              </div>

              {speechFeedback.speakingRate && (
                <div className="bg-slate-700/60 p-3 rounded-md">
                  <p className="font-medium text-slate-100">Speaking Rate:</p>
                  <p className="text-lg font-semibold text-slate-100">
                    {speechFeedback.speakingRate.assessment}
                  </p>
                  {speechFeedback.speakingRate.wpm && (
                    <p className="text-xs text-slate-400">
                      {Math.round(speechFeedback.speakingRate.wpm)} WPM
                    </p>
                  )}
                </div>
              )}
            </div>

            {speechFeedback.positiveFeedback?.length > 0 && (
              <FeedbackSection
                title="What You Did Well"
                initiallyOpen
                color="green"
                headingLevel="h5" // Pass heading level to FeedbackSection
              >
                <ul className="list-disc list-inside space-y-1">
                  {speechFeedback.positiveFeedback.map((fb, i) => (
                    <li
                      key={i}
                      className="text-sm py-1 text-slate-200 text-black"
                    >
                      {fb}
                    </li>
                  ))}
                </ul>
              </FeedbackSection>
            )}

            {speechFeedback.keyAreasToImprove?.length > 0 && (
              <FeedbackSection
                title="Areas to Focus On"
                initiallyOpen
                color="yellow"
                headingLevel="h5"
              >
                <ul className="list-disc list-inside space-y-1">
                  {speechFeedback.keyAreasToImprove.map((area, i) => (
                    <li
                      key={i}
                      className="text-sm py-1 text-slate-200 text-black"
                    >
                      {area}
                    </li>
                  ))}
                </ul>
              </FeedbackSection>
            )}

            {speechFeedback.pronunciationIssues?.length > 0 && (
              <FeedbackSection
                title="Pronunciation Feedback"
                color="orange"
                headingLevel="h5"
              >
                <ul className="space-y-2">
                  {speechFeedback.pronunciationIssues.map((issue, i) => (
                    <li
                      key={i}
                      className="text-sm py-2 border-b border-slate-700 last:border-b-0"
                    >
                      <p className="text-slate-100">
                        <strong className="font-semibold text-amber-300">
                          "{issue.originalWord}"
                        </strong>
                        {issue.spokenAttempt &&
                          ` (You said: "${issue.spokenAttempt}")`}
                      </p>
                      <p className="text-slate-300 mt-1 text-black">
                        {issue.suggestion}
                      </p>
                      {issue.phoneticTip && (
                        <p className="text-xs text-slate-400 mt-1 ">
                          Tip: {issue.phoneticTip}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </FeedbackSection>
            )}

            {speechFeedback.grammarDeviations?.length > 0 && (
              <FeedbackSection
                title="Grammar Feedback"
                color="blue"
                headingLevel="h5"
              >
                <ul className="space-y-2">
                  {speechFeedback.grammarDeviations.map((deviation, i) => (
                    <li
                      key={i}
                      className="text-sm py-2 border-b border-slate-700 last:border-b-0"
                    >
                      <p className="text-slate-300">{deviation.description}</p>{" "}
                      {/* Adjusted contrast */}
                      {deviation.spokenSegment && (
                        <p className="text-red-300 mt-1">
                          Said: "{deviation.spokenSegment}"
                        </p>
                      )}
                      {deviation.correction && (
                        <p className="text-green-300 mt-1">
                          Correct: "{deviation.correction}"
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </FeedbackSection>
            )}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="min-h-screen flex justify-center items-center bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900"
      >
        <Loader message={`Loading details for session #${sessionId}...`} />
      </div>
    );
  }

  if (error) {
    return (
      <main
        role="alert"
        aria-live="assertive"
        ref={errorDisplayRef}
        tabIndex={-1}
        className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white p-4 sm:p-8 text-center outline-none"
      >
        <h1 className="text-3xl sm:text-4xl font-bold mb-4 text-red-300">
          Error
        </h1>
        <p className="text-slate-300">{error}</p>
        <button
          onClick={() => navigate("/dashboard/english-history")}
          className="mt-6 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 font-semibold rounded-lg border border-slate-600 flex items-center mx-auto"
        >
          <ArrowLeftIcon aria-hidden="true" className="w-4 h-4 mr-2" />
          Back to History
        </button>
      </main>
    );
  }

  if (!sessionDetails) {
    return (
      <main
        ref={mainContentRef}
        tabIndex={-1}
        className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white p-4 sm:p-8 text-center outline-none"
      >
        <p className="text-slate-300">Session details not found.</p>
        <button
          onClick={() => navigate("/dashboard/english-history")}
          className="mt-6 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 font-semibold rounded-lg border border-slate-600 flex items-center mx-auto"
        >
          <ArrowLeftIcon aria-hidden="true" className="w-4 h-4 mr-2" />
          Back to History
        </button>
      </main>
    );
  }

  const proficiencyEvaluation = parseJsonSafely(
    sessionDetails.proficiencyEvaluation,
  );
  const richTranscription = parseJsonSafely(sessionDetails.richTranscription);

  return (
    <main
      ref={mainContentRef}
      tabIndex={-1}
      className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-slate-100 p-4 sm:p-8 outline-none"
    >
      <header className="mb-10 flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-100">
            {sessionDetails.planTitle || `Session #${sessionDetails.id}`}
          </h1>
          <p className="text-slate-400 text-sm">
            Created: {formatDate(sessionDetails.createUTC)}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() =>
              navigate(`/dashboard/english-practice?redoSessionId=${sessionId}`)
            }
            className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white font-semibold rounded-lg transition-all duration-300 text-sm flex items-center shadow-lg"
          >
            <RefreshCwIcon className="w-4 h-4 mr-2" />
            Redo Assessment
          </button>
          <button
            onClick={() => navigate("/dashboard/english-history")}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 font-semibold rounded-lg border border-slate-600 transition-all duration-300 text-sm flex items-center"
          >
            <ArrowLeftIcon aria-hidden="true" className="w-4 h-4 mr-2" />
            Back to History
          </button>
        </div>
      </header>

      {/* Session Overview */}
      <section
        aria-labelledby="session-overview-heading"
        className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8"
      >
        <h2 id="session-overview-heading" className="sr-only">
          Session Overview
        </h2>
        {sessionDetails.estimatedProficiencyTarget && (
          <DashboardCard
            title="Target Level"
            icon={<LanguageIcon aria-hidden="true" className="w-full h-full" />}
            titleAs="h3"
          >
            <p className="text-2xl font-bold text-amber-300">
              {proficiencyLevelToString(
                sessionDetails.estimatedProficiencyTarget,
              )}
            </p>
          </DashboardCard>
        )}

        {proficiencyEvaluation?.estimatedProficiencyLevel && (
          <DashboardCard
            title="Current Level (at time of assessment)"
            icon={<LanguageIcon aria-hidden="true" className="w-full h-full" />}
            titleAs="h3"
          >
            <p className="text-2xl font-bold text-sky-300">
              {proficiencyEvaluation.estimatedProficiencyLevel}
            </p>
          </DashboardCard>
        )}
      </section>

      {/* Overall Goal */}
      {sessionDetails.overallGoal && (
        <section aria-labelledby="session-goal-heading" className="mb-6">
          <DashboardCard
            title="Session Goal"
            icon={<LanguageIcon aria-hidden="true" className="w-full h-full" />}
            titleAs="h2"
            id="session-goal-heading"
          >
            <p className="text-slate-200">{sessionDetails.overallGoal}</p>
          </DashboardCard>
        </section>
      )}

      {/* Overall Assessment */}
      {proficiencyEvaluation && (
        <section aria-labelledby="overall-assessment-heading" className="mb-8">
          <DashboardCard
            title="Overall Assessment Summary"
            icon={<LanguageIcon aria-hidden="true" className="w-full h-full" />}
            titleAs="h2"
            id="overall-assessment-heading"
          >
            <div className="space-y-4">
              {richTranscription?.fullTranscript && (
                <div className="mb-4">
                  <h3 className="font-semibold text-slate-100 mb-2">
                    Initial Assessment Response:
                  </h3>
                  <blockquote className="text-slate-300 bg-slate-800 p-3 rounded-md text-sm italic border-l-4 border-slate-600">
                    "{richTranscription.fullTranscript}"
                  </blockquote>
                  {sessionDetails.audioUrl && (
                    <div className="mt-2">
                      <audio
                        controls
                        src={OBJECT_BASE_URL + sessionDetails.audioUrl}
                        className="w-full h-8"
                      />
                    </div>
                  )}
                </div>
              )}
              {proficiencyEvaluation.strengths?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-green-300 mb-2">
                    Strengths:
                  </h3>
                  <ul className="list-disc list-inside space-y-1">
                    {proficiencyEvaluation.strengths.map((strength, i) => (
                      <li key={i} className="text-sm text-slate-200">
                        {strength}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {proficiencyEvaluation.areasForImprovement?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-yellow-300 mb-2">
                    Areas for Improvement:
                  </h3>
                  <ul className="list-disc list-inside space-y-1">
                    {proficiencyEvaluation.areasForImprovement.map(
                      (area, i) => (
                        <li key={i} className="text-sm text-slate-200">
                          {area}
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )}

              {proficiencyEvaluation.detailedFeedback && (
                <div>
                  <h3 className="font-semibold text-blue-300 mb-2">
                    Detailed Feedback:
                  </h3>
                  <p className="text-sm text-slate-200">
                    {proficiencyEvaluation.detailedFeedback}
                  </p>
                </div>
              )}
            </div>
          </DashboardCard>
        </section>
      )}

      {/* Tasks */}
      <section aria-labelledby="practice-tasks-heading">
        <h2
          id="practice-tasks-heading"
          className="text-2xl font-bold mb-4 text-slate-100"
        >
          Practice Tasks
        </h2>
        <div className="space-y-6">
          {sessionDetails.items?.length > 0 ? (
            sessionDetails.items.map((task, index) => (
              <DashboardCard
                key={task.id || index}
                title={`${index + 1}. ${task.title}`}
                icon={
                  <LanguageIcon aria-hidden="true" className="w-full h-full" />
                }
                titleAs="h3"
              >
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="bg-slate-700 px-2 py-1 rounded-full text-slate-200">
                      {task.taskType?.replace("_", " ").toUpperCase()}
                    </span>
                    <span className="bg-blue-700 px-2 py-1 rounded-full text-slate-100">
                      {task.difficultyLevel}
                    </span>
                  </div>

                  {task.instructions && (
                    <div className="bg-slate-700/30 p-3 rounded-md">
                      <h4 className="font-semibold text-sky-300 mb-2">
                        Instructions:
                      </h4>
                      <p className="text-slate-200 text-sm italic">
                        {task.instructions}
                      </p>
                    </div>
                  )}

                  <div className="bg-slate-700/30 p-3 rounded-md">
                    <h4 className="font-semibold text-sky-300 mb-2">
                      Objective:
                    </h4>
                    <p className="text-slate-200 text-sm">{task.objective}</p>
                  </div>

                  <div className="bg-slate-700/30 p-3 rounded-md">
                    <h4 className="font-semibold text-sky-300 mb-2">
                      Task Content:
                    </h4>
                    {renderTaskContent(task)}
                  </div>

                  {renderSpeechFeedback(task)}
                </div>
              </DashboardCard>
            ))
          ) : (
            <DashboardCard>
              <p className="text-center text-slate-400">
                No tasks found for this session.
              </p>
            </DashboardCard>
          )}
        </div>
      </section>
    </main>
  );
};

export default EnglishPracticeSessionDetailPage;
